import type { ReactNode } from 'react';

// Il markdown che scrivono gli agenti, reso come elementi React: titoli, elenchi, tabelle,
// paragrafi, blocchi di codice, grassetto, corsivo e codice in linea. Niente HTML grezzo: il testo viene
// da un modello, quindi non passa mai da innerHTML.

function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  // `_corsivo_` solo a parola intera: negli id (`sub_01M…`) i trattini bassi restano tali
  // `[testo](indirizzo)`: un link solo per http(s); un percorso relativo (un file dello spazio di
  // lavoro, es. `[GUIDA.md](GUIDA.md)`) resta il suo nome, come codice in linea
  const re =
    /(\[[^\]\n]+\]\([^)\s]+\)|\*\*[^*]+\*\*|`[^`]+`|(?<![\p{L}\p{N}])_[^_\n]+_(?![\p{L}\p{N}])|\*[^*\s][^*]*\*)/gu;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const k = `${key}-${i++}`;
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
    if (link) {
      const [, label, href] = link;
      out.push(
        /^https?:\/\//i.test(href) ? (
          <a key={k} href={href} target="_blank" rel="noopener noreferrer">
            {label}
          </a>
        ) : (
          <code
            key={k}
            className="t-mono-xs"
            style={{ background: 'var(--bg-2)', padding: '0 4px', borderRadius: 4 }}
          >
            {label}
          </code>
        ),
      );
    } else if (tok.startsWith('**')) out.push(<strong key={k}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`'))
      out.push(
        <code
          key={k}
          className="t-mono-xs"
          style={{ background: 'var(--bg-2)', padding: '0 4px', borderRadius: 4 }}
        >
          {tok.slice(1, -1)}
        </code>,
      );
    else out.push(<em key={k}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'h'; level: number; text: string }
  | { kind: 'ul' | 'ol'; items: string[] }
  | { kind: 'code'; text: string }
  | { kind: 'table'; head: string[]; rows: string[][] };

// una riga di tabella: | a | b | → ['a', 'b']
const cells = (line: string) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
const isRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
const isRule = (l: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l);

function blocks(src: string): Block[] {
  const out: Block[] = [];
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim().startsWith('```')) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) body.push(lines[i++]);
      out.push({ kind: 'code', text: body.join('\n') });
      i++;
      continue;
    }
    if (isRow(line) && i + 1 < lines.length && isRule(lines[i + 1])) {
      const head = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isRow(lines[i])) rows.push(cells(lines[i++]));
      out.push({ kind: 'table', head, rows });
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      out.push({ kind: 'h', level: h[1].length, text: h[2] });
      i++;
      continue;
    }
    const isUl = (l: string) => /^\s*[-*]\s+/.test(l);
    const isOl = (l: string) => /^\s*\d+[.)]\s+/.test(l);
    if (isUl(line) || isOl(line)) {
      const ordered = isOl(line);
      const items: string[] = [];
      while (i < lines.length && (ordered ? isOl(lines[i]) : isUl(lines[i]))) {
        items.push(lines[i].replace(ordered ? /^\s*\d+[.)]\s+/ : /^\s*[-*]\s+/, ''));
        i++;
        // le righe rientrate che seguono continuano la voce
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !isUl(lines[i]) && !isOl(lines[i]))
          items[items.length - 1] += '\n' + lines[i++].trim();
      }
      out.push({ kind: ordered ? 'ol' : 'ul', items });
      continue;
    }
    if (!line.trim()) {
      i++;
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,4}\s|```|\s*[-*]\s|\s*\d+[.)]\s)/.test(lines[i]) &&
      !(isRow(lines[i]) && i + 1 < lines.length && isRule(lines[i + 1]))
    )
      para.push(lines[i++]);
    out.push({ kind: 'p', lines: para });
  }
  return out;
}

export function Markdown({ text }: { text: string }) {
  return (
    <div style={{ display: 'grid', gap: 8, overflowWrap: 'anywhere' }}>
      {blocks(text).map((b, n) => {
        const k = `b${n}`;
        if (b.kind === 'h')
          return (
            <div key={k} className="t-strong" style={{ fontSize: b.level <= 2 ? 15 : 14 }}>
              {inline(b.text, k)}
            </div>
          );
        if (b.kind === 'code')
          return (
            <pre
              key={k}
              className="t-mono-xs"
              style={{
                margin: 0,
                padding: 10,
                background: 'var(--bg-2)',
                borderRadius: 'var(--r-md)',
                overflowX: 'auto',
                whiteSpace: 'pre',
              }}
            >
              {b.text}
            </pre>
          );
        if (b.kind === 'table')
          return (
            <div key={k} className="md-table">
              <table>
                <thead>
                  <tr>
                    {b.head.map((c, j) => (
                      <th key={j}>{inline(c, `${k}-h${j}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.rows.map((r, ri) => (
                    <tr key={ri}>
                      {b.head.map((_, j) => (
                        <td key={j}>{inline(r[j] ?? '', `${k}-${ri}-${j}`)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        if (b.kind === 'ul' || b.kind === 'ol') {
          const List = b.kind;
          return (
            <List key={k} style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 4 }}>
              {b.items.map((it, j) => (
                <li key={j} style={{ whiteSpace: 'pre-wrap' }}>
                  {inline(it, `${k}-${j}`)}
                </li>
              ))}
            </List>
          );
        }
        if (b.kind !== 'p') return null;
        return (
          <p key={k} style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
            {inline(b.lines.join('\n'), k)}
          </p>
        );
      })}
    </div>
  );
}
